/**
 * Strictly Golf — Scroll Reveal & Interaction Controller
 * Lightweight IntersectionObserver for .sg-reveal elements.
 * Also handles CountUp animations for .sg-count-up elements.
 * ES6, no dependencies, lazy-initialized.
 */

(function () {
  'use strict';

  /* Bail early if user prefers reduced motion */
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /**
   * Scroll Reveal — IntersectionObserver
   * Elements with .sg-reveal get .sg-reveal--active when 15% visible.
   */
  function initScrollReveal() {
    const reveals = document.querySelectorAll('.sg-reveal');
    if (!reveals.length) return;

    if (prefersReducedMotion) {
      reveals.forEach(function (el) {
        el.classList.add('sg-reveal--active');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('sg-reveal--active');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    reveals.forEach(function (el) {
      observer.observe(el);
    });
  }

  /**
   * CountUp Animation
   * Elements with [data-count-to] animate from 0 to the target value.
   */
  function initCountUp() {
    const counters = document.querySelectorAll('[data-count-to]');
    if (!counters.length) return;

    if (prefersReducedMotion) {
      counters.forEach(function (el) {
        el.textContent = el.getAttribute('data-count-to');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    counters.forEach(function (el) {
      el.textContent = '0';
      observer.observe(el);
    });
  }

  function animateCount(el) {
    var target = parseInt(el.getAttribute('data-count-to'), 10);
    var suffix = el.getAttribute('data-count-suffix') || '';
    var prefix = el.getAttribute('data-count-prefix') || '';
    var duration = parseInt(el.getAttribute('data-count-duration') || '2000', 10);
    var start = 0;
    var startTime = null;

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      /* Ease out cubic */
      var eased = 1 - Math.pow(1 - progress, 3);
      var current = Math.floor(eased * target);

      el.textContent = prefix + current.toLocaleString() + suffix;

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        el.textContent = prefix + target.toLocaleString() + suffix;
      }
    }

    window.requestAnimationFrame(step);
  }

  /**
   * Hero Slider Controller
   * Manages auto-play, indicators, and slide transitions for .sg-hero-slider.
   */
  function initHeroSlider() {
    var sliders = document.querySelectorAll('.sg-hero-slider');
    sliders.forEach(function (slider) {
      var slides = slider.querySelectorAll('.sg-hero-slider__slide');
      var indicators = slider.querySelectorAll('.sg-hero-slider__indicator');
      var prevBtn = slider.querySelector('.sg-hero-slider__prev');
      var nextBtn = slider.querySelector('.sg-hero-slider__next');
      var autoplaySpeed = parseInt(slider.getAttribute('data-autoplay-speed') || '5000', 10);
      var currentIndex = 0;
      var autoplayTimer = null;
      var isPaused = false;

      if (slides.length <= 1) return;

      function showSlide(index) {
        slides.forEach(function (slide, i) {
          slide.classList.toggle('sg-hero-slider__slide--active', i === index);
          slide.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
        });
        indicators.forEach(function (dot, i) {
          dot.classList.toggle('sg-hero-slider__indicator--active', i === index);
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        currentIndex = index;
      }

      function nextSlide() {
        showSlide((currentIndex + 1) % slides.length);
      }

      function prevSlide() {
        showSlide((currentIndex - 1 + slides.length) % slides.length);
      }

      function startAutoplay() {
        if (autoplayTimer) clearInterval(autoplayTimer);
        if (!prefersReducedMotion) {
          autoplayTimer = setInterval(nextSlide, autoplaySpeed);
        }
      }

      function stopAutoplay() {
        if (autoplayTimer) {
          clearInterval(autoplayTimer);
          autoplayTimer = null;
        }
      }

      /* Event listeners */
      if (nextBtn) nextBtn.addEventListener('click', function () { nextSlide(); startAutoplay(); });
      if (prevBtn) prevBtn.addEventListener('click', function () { prevSlide(); startAutoplay(); });

      indicators.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          showSlide(i);
          startAutoplay();
        });
      });

      /* Pause on hover */
      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', function () {
        if (!isPaused) startAutoplay();
      });

      /* Touch / swipe support */
      var touchStartX = 0;
      var touchEndX = 0;

      slider.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      slider.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        var diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) nextSlide();
          else prevSlide();
          startAutoplay();
        }
      }, { passive: true });

      /* Keyboard navigation */
      slider.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') { nextSlide(); startAutoplay(); }
        if (e.key === 'ArrowLeft') { prevSlide(); startAutoplay(); }
      });

      /* Initialize */
      showSlide(0);
      startAutoplay();
    });
  }

  /**
   * FAQ Accordion Controller
   * Manages expand/collapse for .sg-accordion-item elements.
   */
  function initAccordions() {
    var accordions = document.querySelectorAll('.sg-accordion');
    accordions.forEach(function (accordion) {
      var items = accordion.querySelectorAll('.sg-accordion-item');
      var allowMultiple = accordion.hasAttribute('data-allow-multiple');

      items.forEach(function (item) {
        var trigger = item.querySelector('.sg-accordion-item__trigger');
        var content = item.querySelector('.sg-accordion-item__content');

        if (!trigger || !content) return;

        trigger.addEventListener('click', function () {
          var isOpen = item.classList.contains('sg-accordion-item--open');

          if (!allowMultiple) {
            items.forEach(function (otherItem) {
              if (otherItem !== item) {
                otherItem.classList.remove('sg-accordion-item--open');
                var otherContent = otherItem.querySelector('.sg-accordion-item__content');
                if (otherContent) otherContent.style.maxHeight = '0';
                var otherTrigger = otherItem.querySelector('.sg-accordion-item__trigger');
                if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
              }
            });
          }

          if (isOpen) {
            item.classList.remove('sg-accordion-item--open');
            content.style.maxHeight = '0';
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('sg-accordion-item--open');
            content.style.maxHeight = content.scrollHeight + 'px';
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---- Initialize on DOM ready ---- */
  function init() {
    initScrollReveal();
    initCountUp();
    initHeroSlider();
    initAccordions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-init on Shopify section load/select (theme editor) */
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
